import {
  DOCUMENT,
  Location,
  LocationStrategy,
  isPlatformBrowser,
  isPlatformServer,
  DatePipe,
} from "@angular/common";
import {
  PLATFORM_ID,
  AfterViewInit,
  HostListener,
  Component,
  ViewEncapsulation,
  Inject,
  WritableSignal,
  ViewChild,
  ElementRef,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";

import {
  Seo,
  Push,
  CheckForUpdate,
  LogUpdate,
  PromptUpdate,
} from "./shared/utils";

import { FooterComponent } from "./shared/ui/footer";
import { FilterPipe } from "./shared/pipes/filter";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import { environment } from "../environments/environment";
import hljs from "highlight.js";

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

declare const ga: () => void;
/* eslint-disable @typescript-eslint/no-unused-vars */
const xxl = 1400;
const xl = 1200;
const l = 1000;
const m = 800;
const s = 600;
const xs = 400;
/* eslint-enable @typescript-eslint/no-unused-vars */

@Component({
  selector: "app-root",
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["./app.component.sass"],
  templateUrl: "./app.component.html",
  standalone: true,
  imports: [DatePipe],
})
export class AppComponent implements AfterViewInit {
  @ViewChild("macbook") macbook: ElementRef;
  @ViewChild("loader") loader: ElementRef;
  public environment = environment;
  public currentDate: WritableSignal<Date> = signal(new Date());
  public application: WritableSignal<String> = signal("");

  private booted: Boolean = false;
  private routerSubscription: Subscription;

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    if (event.target.innerWidth < xl) {
      this.macbook?.nativeElement.classList.add("notransition");
      this.hover(this.macbook?.nativeElement.classList.contains("hovered"));
    }
  }

  @HostListener("window:keyDown", ["$event"])
  onKeyPress(event: KeyboardEvent) {
    if (isPlatformBrowser(this.platformId)) {
      if (event.key === "p" || event.key === "P") {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const registration of registrations) {
            registration.unregister();
          }
          console.log("Service worker purged");
          window.location.reload();
        });
      }
    }
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document,
    public router: Router,
    public url: LocationStrategy,
    public seo: Seo,
    public push: Push,
    public checkForUpdate: CheckForUpdate,
    public logUpdate: LogUpdate,
    public promptUpdate: PromptUpdate,
    private location: Location,
  ) {
    this.routerSubscription = this.router.events.subscribe((event) => {
      /**
       *  Send page to google analytics on page change.
       */
      if (event instanceof NavigationEnd && typeof ga !== "undefined") {
        // ga('send', 'pageview', event.urlAfterRedirects.split('?')[0]);
      }
    });

    this.seo.setDescription(environment.description);

    if (isPlatformBrowser(platformId)) {
      setInterval(() => {
        this.currentDate.set(new Date());
      }, 1000);
    }
  }

  public hover = (async (hovered = false) => {
    if (!this.macbook?.nativeElement.classList.contains("hovered")) {
      this.macbook?.nativeElement.classList.add("hovered");
      await this.boot();
    }
  }).bind(this);

  public ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Client only code.
      console.log("Execute browser code");
      this.openMacbook();
    }

    if (isPlatformServer(this.platformId)) {
      // Server only code.
      console.log("Execute server code");
      // ...
    }
  }

  private openMacbook = (async () => {
    // Script to keep the macbook opened once hovered -->
    // this.macbook?.nativeElement.addEventListener("mouseover", this.hover);

    await delay(1000);

    // Script to keep the macbook / ipad / iphone opened if window size is too small -->
    this.macbook?.nativeElement.classList.remove("hide");
    this.macbook?.nativeElement.classList.remove("transparent");
    this.loader?.nativeElement.classList.add("hide");

    const w = window;
    const d = this.document;
    const e = d.documentElement;
    const g = d.getElementsByTagName("body")[0];
    const x = w.innerWidth || e.clientWidth || g.clientWidth;
    // const y = w.innerHeight || e.clientHeight || g.clientHeight;

    if (x < xl) {
      this.macbook?.nativeElement.classList.add("notransition");
      this.hover(this.macbook?.nativeElement.classList.contains("hovered"));
    }
  }).bind(this);

  public async boot() {
    console.log("Booting....");
    if (this.booted) {
      return;
    }

    this.booted = true;

    await delay(3000);

    await this.initSystem();

    await delay(4000);

    this.application.set("Terminal");
    await this.initTerminal();

    await delay(2000);

    const terminal = document
      .getElementById("terminal")
      .getElementsByClassName("window-content")[0]
      .getElementsByClassName("typewriter")[0];

    terminal.innerHTML += `<span class="command">>_ <span></span></span>`;

    await delay(1500);

    await this.typewrite(
      terminal
        .getElementsByClassName("command")[0]
        .getElementsByTagName("span")[0],
      `find "Quentin Petel" -print > curriculumvitae.html `,
      "bash",
    );
    terminal.innerHTML += `<br><br>`;

    terminal.innerHTML += `<div class="return"><span></span><span></span></div>`;
    await delay(1000);
    terminal
      .getElementsByClassName("return")[0]
      .getElementsByTagName("span")[0].innerText =
      "Initializing system................";
    await delay(1000);
    // element.getElementsByClassName('return')[0].getElementsByTagName('span')[1].innerText = 'OK';

    // terminal.innerHTML += `<div class="return"><span></span><span></span></div>`;
    // await delay(1000);
    // terminal
    //   .getElementsByClassName("return")[1]
    //   .getElementsByTagName("span")[0].innerText =
    //   "Connecting to the matrix...........";
    // await delay(1000);
    // // element.getElementsByClassName('return')[1].getElementsByTagName('span')[1].innerText = 'OK';

    // await this.initMatrix();

    terminal.innerHTML += `<div class="return"><span></span><span></span></div>`;
    await delay(1000);
    terminal
      .getElementsByClassName("return")[1]
      .getElementsByTagName("span")[0].innerText =
      "Fetching data......................";
    await delay(2500);
    // element.getElementsByClassName('return')[2].getElementsByTagName('span')[1].innerText = 'OK';

    terminal.innerHTML += `<div class="return"><span></span><span></span></div>`;
    await delay(1000);
    terminal
      .getElementsByClassName("return")[2]
      .getElementsByTagName("span")[0].innerText =
      "Compiling results..................";
    await delay(1000);
    // element.getElementsByClassName('return')[3].getElementsByTagName('span')[1].innerText = 'OK';

    terminal.innerHTML += `<br>`;
    terminal.innerHTML += `<span class="command">>_ <span></span></span>`;

    await delay(1500);

    await this.typewrite(
      terminal
        .getElementsByClassName("command")[1]
        .getElementsByTagName("span")[0],
      `ll `,
      "bash",
    );
    terminal.innerHTML += `<br>`;

    await delay(1000);
    terminal.innerHTML += `curriculumvitae.html<br>`;

    terminal.innerHTML += `<br>`;
    terminal.innerHTML += `<span class="command">>_ <span></span></span>`;

    await delay(1500);

    await this.typewrite(
      terminal
        .getElementsByClassName("command")[2]
        .getElementsByTagName("span")[0],
      `open curriculumvitae.html `,
      "bash",
    );
    terminal.innerHTML += `<br>`;

    await delay(1000);

    terminal.innerHTML += `<br>`;
    terminal.innerHTML += `<span class="command">>_ <span></span></span>`;

    this.application.set("Browser");
    await this.initResults();

    await delay(120000);

    document.getElementById("terminal").style.zIndex = "3000";
    await delay(1500);
    this.application.set("Terminal");

    await this.typewrite(
      terminal
        .getElementsByClassName("command")[3]
        .getElementsByTagName("span")[0],
      `:(){ :|:& };: `,
      "bash",
    );
    terminal.innerHTML += `<br>`;

    await delay(3000);

    this.application.set("");
    await this.closeTerminal();
    await this.closeResults();

    await delay(1000);

    await this.closeSystem();

    this.booted = false;
    this.boot();
  }

  public async closeSystem() {
    const element = document.getElementById("main");
    element.style.display = "none";
  }

  public async initSystem() {
    const element = document.getElementById("main");
    element.style.display = "flex";
  }

  public async closeTerminal() {
    const element = document.getElementById("terminal");
    element.style.display = "none";
    document.getElementById("terminal").style.zIndex = "initial";

    const terminal = element
      .getElementsByClassName("window-content")[0]
      .getElementsByClassName("typewriter")[0];
    terminal.innerHTML = "";
  }

  public async initTerminal() {
    const element = document.getElementById("terminal");
    element.style.display = "flex";
  }

  public async closeResults() {
    const element = document.getElementById("results");
    element.style.display = "none";
  }

  public async initResults() {
    const element = document.getElementById("results");
    element.style.display = "flex";

    //     const text =
    //       `\
    // @QuentinPetel({
    //   age:      ` +
    //       (() => {
    //         // birthday is a date
    //         const ageDifMs = Date.now() - new Date("1990-02-25").getTime();
    //         const ageDate = new Date(ageDifMs); // miliseconds from epoch
    //         return Math.abs(ageDate.getUTCFullYear() - 1970);
    //       })() +
    //       `,
    //   country:  'France',
    //   city:     'Nantes',
    //   company: {
    //     name:     'IT-era',
    //     roles: [
    //               'Project Engineer',
    //               'Lead Technical Consultant',
    //               'Full-Stack dev',
    //     ],
    //     url:      'https://it-era.dev',
    //     email:    'contact@it-era.dev',
    //     phone:    '+33 (0)6 32 52 62 75',
    //   },
    //   skills: {
    //     technos: [
    //       docker:    '[████████████]',
    //       react:     '[████████████]',
    //       node:      '[████████████]',
    //       angular:   '[███████████ ]',
    //       laravel:   '[████████████]',
    //       vuejs:     '[██████████  ]',
    //       meteor:    '[██████████  ]',
    //       // ...
    //     ],
    //     languages: [
    //       js/ts:     '[████████████]',
    //       php:       '[████████████]',
    //       html/css:  '[████████████]',
    //       mongodb:   '[████████████]',
    //       sql:       '[██████████  ]',
    //       // ...
    //     ],
    //   },
    //   links: {
    //     Web:           'https://quentinpetel.fr',
    //     LinkedIn:      'https://bit.ly/qp_lnkdn',
    //     GitHub:        'https://bit.ly/qp_gthb',
    //     StackOverflow: 'https://bit.ly/qp_stckvrflw',
    //   },
    // })

    // `;

    // await this.typewrite(
    //   element
    //     .getElementsByClassName("window-content")[0]
    //     .getElementsByClassName("typewriter")[0],
    //   text,
    //   "typescript",
    //   { speed: 0 },
    // );
  }

  public async initProfile() {
    const element = document.getElementById("profile");
    element.style.display = "flex";
    setInterval(async () => {
      element.style.background =
        'url("/assets/img/profile-inverse.jpg") no-repeat';
      element.style.backgroundSize = "cover";
      await delay(Math.random() * 100);
      element.style.background = 'url("/assets/img/profile.jpg") no-repeat';
      element.style.backgroundSize = "cover";
    }, Math.random() * 20000);

    const cvs = element.getElementsByTagName("canvas")[0];
    const ctx = cvs.getContext("2d");
    const img = new Image();
    img.src = "/assets/img/profile-inverse.jpg";

    if (img.complete) {
      processImage(img);
    } else {
      img.onload = () => {
        processImage(img);
      };
    }

    function processImage(image) {
      const w = image.width;
      const h = image.height;
      cvs.style.width = w;
      cvs.style.padding = "0";
      cvs.width = w;
      cvs.height = h;
      ctx.drawImage(image, 0, 0);
      // const px = w * h;
      const data = ctx.getImageData(0, 0, w, h);
      const len = data.data.length;
      // const rate = 0.01;

      animate();

      function animate() {
        requestAnimationFrame(animate);
        for (let i = len - 5; i > 0; i -= 4) {
          const relI = i * 0.25;
          const y = Math.floor(relI / w);
          let x = Math.random() > 0.5 ? relI - 1 : relI + 1;
          x = (x % w) + w * y;
          x *= 4;
          data.data[x] = data.data[i];
          data.data[x + 1] = data.data[i + 1];
          data.data[x + 2] = data.data[i + 2];
          data.data[x + 3] = data.data[i + 3];
        }
        ctx.putImageData(data, 0, 0);
      }
    }
  }

  public async initMatrix() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const q: any = document.getElementById("matrix");

    const width = q.width;
    const height = q.height;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const one: any = 1;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const letters: any[] = Array(256).join(one).split("");

    console.log(letters);

    const draw = (() => {
      q.getContext("2d").fillStyle = "rgba(0,0,0,.05)";
      q.getContext("2d").fillRect(0, 0, width, height);
      q.getContext("2d").fillStyle = "#5b6078";
      letters.map((yPos, index) => {
        const text = String.fromCharCode(3e4 + Math.random() * 33);
        const xPos = index * 10;
        q.getContext("2d").fillText(text, xPos, yPos);
        letters[index] = yPos > 758 + Math.random() * 1e4 ? 0 : yPos + 10;
      });
    }).bind(this);

    setInterval(draw, 40);
  }

  public async typewrite(
    element: Element,
    text: string,
    lang: string,
    options?,
  ) {
    let HTML = ``;
    element.innerHTML = "";

    let cursorPosition = 0;
    const typeSpeed = options?.speed ?? 60;
    let tempTypeSpeed = 0;

    while (cursorPosition < text.length - 1) {
      if (
        text[cursorPosition] === " " ||
        text[cursorPosition] === "." ||
        options?.speed === 0
      ) {
        tempTypeSpeed = 0;
      } else {
        tempTypeSpeed = Math.random() * typeSpeed + 30;
      }

      HTML += text[cursorPosition];
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      tempTypeSpeed && (await delay(tempTypeSpeed));
      cursorPosition += 1;
      // if (cursorPosition < text.length - 1) {
      //     setTimeout(type, tempTypeSpeed);
      // }

      element.innerHTML = hljs
        .highlight(HTML, { language: lang })
        .value.replaceAll(/&#x27;(.*@.+)&#x27;/g, (value) => {
          return `<a href="mailto:${value.replaceAll(
            "&#x27;",
            "",
          )}"  target="_blank" rel="noreferrer noopener">${value}</a>`;
        })
        .replaceAll(/&#x27;(https?:\/\/.+)&#x27;/g, (value) => {
          return `<a href="${value.replaceAll(
            "&#x27;",
            "",
          )}"  target="_blank" rel="noreferrer noopener">${value}</a>`;
        });
    }
  }
}
